// File: frontend/src/components/Layout/Layout.jsx
// Main layout component with sidebar navigation for WA Contact Center CRM

import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from 'react-query';
import api from '../../services/api';
import WALogo from '../UI/WALogo';
import {
  HomeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CalendarIcon,
  DocumentChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch real data for sidebar badges
  const { data: sidebarData } = useQuery(
    'sidebar-stats',
    async () => {
      const [leadsRes, oppStatsRes] = await Promise.all([
        api.get('/leads?limit=1'),
        api.get('/opportunities/stats/dashboard')
      ]);

      const totalLeads = leadsRes.data?.pagination?.total_items || 0;

      // Pipeline value straight from the opportunities stats endpoint
      // (response shape: { success, data: { basic: { pipeline_value, total_opportunities, ... }, ... } })
      const basic = oppStatsRes.data?.data?.basic || {};
      const pipelineValue = parseFloat(basic.pipeline_value || 0);
      const totalOpportunities = parseInt(basic.total_opportunities || 0);

      return {
        totalLeads,
        totalOpportunities,
        pipelineValue: Math.round(pipelineValue / 1000) // Convert to thousands
      };
    },
    {
      enabled: !!user?.id,
      refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
      retry: 1,
    }
  );

  // Navigation items based on user permissions
  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      show: true,
    },
    {
      name: 'Leads',
      href: '/leads',
      icon: UserGroupIcon,
      show: hasPermission('leads', 'read'),
      badge: sidebarData?.totalLeads ? sidebarData.totalLeads.toString() : null
    },
    {
      name: 'Contatos',
      href: '/contacts',
      icon: UserGroupIcon,
      show: hasPermission('contacts', 'read'),
    },
    {
      name: 'Empresas',
      href: '/companies',
      icon: BuildingOfficeIcon,
      show: hasPermission('companies', 'read'),
    },
    {
      name: 'Pipeline',
      href: '/pipeline',
      icon: ChartBarIcon,
      show: hasPermission('opportunities', 'read'),
      badge: sidebarData?.pipelineValue ? `R$ ${sidebarData.pipelineValue}k` : null
    },
    {
      name: 'Oportunidades',
      href: '/opportunities',
      icon: ChartBarIcon,
      show: hasPermission('opportunities', 'read'),
      badge: sidebarData?.totalOpportunities ? sidebarData.totalOpportunities.toString() : null
    },
    {
      name: 'Atividades',
      href: '/activities',
      icon: CalendarIcon,
      show: hasPermission('activities', 'read'),
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: DocumentChartBarIcon,
      show: hasPermission('reports', 'read'),
    },
    {
      name: 'Relatórios',
      href: '/reports',
      icon: DocumentChartBarIcon,
      show: hasPermission('reports', 'read'),
    },
    {
      name: 'Usuários',
      href: '/users',
      icon: UserGroupIcon,
      show: hasPermission('users', 'read'),
    },
    {
      name: 'Configurações',
      href: '/settings',
      icon: CogIcon,
      show: hasPermission('settings', 'read'),
    },
  ].filter(item => item.show);

  const isCurrentPage = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-900 shadow-xl">
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-800">
            <WALogo size="md" showText={true} />
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  isCurrentPage(item.href)
                    ? 'bg-wa-red/10 dark:bg-wa-red/20 border-wa-red text-wa-red dark:text-wa-red'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
                } group flex items-center px-2 py-2 text-sm font-medium border-l-4 rounded-md`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon
                  className={`${
                    isCurrentPage(item.href) ? 'text-wa-red dark:text-wa-red' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                  } mr-3 h-6 w-6`}
                />
                {item.name}
                {item.badge && (
                  <span className="ml-auto inline-block py-0.5 px-2 text-xs rounded-full bg-wa-red/10 text-wa-red font-medium">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800">
        <div className="flex items-center h-16 px-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <WALogo size="lg" showText={false} />
            <div className="flex flex-col">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-wa-red text-xl">WA</span>
                <span className="text-sm font-semibold text-wa-gray dark:text-white">Contact Center</span>
              </div>
              <span className="text-xs text-wa-red font-medium -mt-1">CRM System</span>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-4 py-4 space-y-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`${
                isCurrentPage(item.href)
                  ? 'bg-wa-red/10 dark:bg-wa-red/20 border-wa-red text-wa-red dark:text-wa-red'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
              } group flex items-center px-2 py-2 text-sm font-medium border-l-4 rounded-md`}
            >
              <item.icon
                className={`${
                  isCurrentPage(item.href) ? 'text-wa-red dark:text-wa-red' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                } mr-3 h-6 w-6`}
              />
              {item.name}
              {item.badge && (
                <span className="ml-auto inline-block py-0.5 px-2 text-xs rounded-full bg-wa-red/10 text-wa-red font-medium">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
          <button
            type="button"
            className="px-4 text-gray-500 hover:text-gray-600 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex-1 flex items-center justify-end px-4">
            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* User menu */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center space-x-3 text-gray-700 hover:text-gray-900"
                >
                  <div className="flex-shrink-0">
                    <img
                      className="h-8 w-8 rounded-full"
                      src={user?.avatar_url || `https://ui-avatars.com/api/?name=${user?.first_name}+${user?.last_name}&background=3B82F6&color=fff`}
                      alt={`${user?.first_name} ${user?.last_name}`}
                    />
                  </div>
                  <div className="hidden md:block">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.first_name} {user?.last_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{user?.role?.name?.replace('_', ' ')}</div>
                  </div>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => setShowUserDropdown(false)}
                    >
                      Ver Perfil
                    </Link>
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => setShowUserDropdown(false)}
                    >
                      Configurações
                    </Link>
                    <button
                      onClick={() => {
                        setShowUserDropdown(false);
                        logout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;